// src/UpgradePlan.js
// Confirmation screen for Individual → Family upgrade with Stripe proration

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from './config';
import { useAuth } from './AuthContext';
import AppHeader from './AppHeader';

export default function UpgradePlan() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/stripe/upgrade-preview`, {
      headers: { 'Authorization': `Bearer ${user.token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); }
        else { setPreview(data); }
      })
      .catch(() => setError('Could not load upgrade details.'))
      .finally(() => setLoading(false));
  }, [user]);

  const handleUpgrade = async () => {
    setUpgrading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/stripe/upgrade-to-family`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Upgrade failed.');
        setUpgrading(false);
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/parent/link-student'), 2500);
    } catch {
      setError('Network error — please try again.');
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <>
        <AppHeader subtitle="Upgrade Plan" />
        <div style={s.page}><p style={s.loadingText}>Loading upgrade details…</p></div>
      </>
    );
  }

  if (success) {
    return (
      <>
        <AppHeader subtitle="Upgrade Plan" />
        <div style={s.page}>
          <div style={s.card}>
            <div style={s.successIcon}>✅</div>
            <h1 style={s.title}>You're on the Family Plan!</h1>
            <p style={s.subtitle}>You can now add up to 3 students. Redirecting to Add a Student…</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AppHeader subtitle="Upgrade Plan" />
      <div style={s.page}>
        <div style={s.card}>
          <button onClick={() => navigate('/parent/dashboard')} style={s.backBtn}>
            ← Back to Dashboard
          </button>

          <h1 style={s.title}>Upgrade to Family Plan</h1>
          <p style={s.subtitle}>
            Add up to 3 students under one account. Each child gets their own profile and personalized matches.
          </p>

          {error && <div style={s.error}>{error}</div>}

          {preview && (
            <div style={s.previewBox}>
              <div style={s.previewRow}>
                <span style={s.previewLabel}>Current plan</span>
                <span style={s.previewValue}>Individual ($47/yr)</span>
              </div>
              <div style={s.previewRow}>
                <span style={s.previewLabel}>New plan</span>
                <span style={s.previewValue}>Family ($97/yr)</span>
              </div>
              <div style={{ ...s.previewRow, borderTop: '2px solid #E5E7EB', paddingTop: 14, marginTop: 6 }}>
                <span style={{ ...s.previewLabel, fontWeight: 700, color: '#111827' }}>Due today (prorated)</span>
                <span style={{ ...s.previewValue, fontWeight: 700, color: '#2563EB', fontSize: 20 }}>
                  ${preview.prorationAmount.toFixed(2)}
                </span>
              </div>
              <p style={s.previewNote}>
                At renewal on {new Date(preview.renewalDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}, you'll be charged the full $97/yr Family rate.
              </p>
            </div>
          )}

          {preview && (
            <button
              onClick={handleUpgrade}
              disabled={upgrading}
              style={{ ...s.upgradeBtn, opacity: upgrading ? 0.6 : 1 }}
            >
              {upgrading ? 'Upgrading…' : `Upgrade Now — $${preview.prorationAmount.toFixed(2)}`}
            </button>
          )}

          <p style={s.footerNote}>
            Questions? Email <a href="mailto:theteam@yourscholarshipfinder.com" style={s.link}>theteam@yourscholarshipfinder.com</a>
          </p>
        </div>
      </div>
    </>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#F3F4F6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '40px',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    alignSelf: 'flex-start',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#6B7280',
    fontSize: '13px',
    cursor: 'pointer',
    padding: 0,
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  error: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '8px',
    padding: '12px',
    color: '#DC2626',
    fontSize: '13px',
    marginBottom: '16px',
  },
  previewBox: {
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '24px',
  },
  previewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
  },
  previewLabel: {
    fontSize: '14px',
    color: '#6B7280',
  },
  previewValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
  },
  previewNote: {
    fontSize: '12px',
    color: '#9CA3AF',
    marginTop: '12px',
    lineHeight: '1.5',
  },
  upgradeBtn: {
    width: '100%',
    padding: '14px',
    background: '#2563EB',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  footerNote: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#9CA3AF',
  },
  link: {
    color: '#2563EB',
    textDecoration: 'underline',
  },
  loadingText: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '14px',
    paddingTop: '100px',
  },
  successIcon: {
    fontSize: '48px',
    textAlign: 'center',
    marginBottom: '16px',
  },
};